import ClickAwayListener from '@components/App/ClickAwayListener/ClickAwayListener'
import useOnDragStart from '@components/ArtBoard/DragAndDrop/hooks/useOnDragStart'
import { DRAGGABLE_TYPE } from '@components/ArtBoard/DragAndDrop/types'
import type { BlockWireframe, ID } from '@octopus-do/octopus-project-core'
import {
  thunkCheckUsageAndDeleteSymbol,
  thunkDetachSymbol,
  thunkUpdateSymbolColor,
  thunkUpdateSymbolTitle,
  thunkUpdateSymbolVisual,
  thunkUpdateSymbolWireframes,
  thunkUploadSymbolImage,
} from '@store/migrationActions/thunkActions/thunkSymbolActions'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import {
  CanvasActionTypes,
  setUploadingImageSymbol,
} from '@storeOld/reducers/canvas/canvas'
import { getPreviewColor } from '@storeOld/reducers/canvas/selectors'
import type React from 'react'
import { useRef } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import type { Action } from 'redux'
import type { ThunkDispatch } from 'redux-thunk'

import { useArtBoardDisabled } from '@/hooks/selectors/useArtBoardDisabled'
import type { AppState } from '@/types'
import { isInsidePopover } from '@/utils'
import { SymbolCard } from '../../../SymbolCard'
import { SymbolToolbar } from '../../../SymbolToolbar'
import { Note } from './components/Note'

import './Symbol.scss'

type Props = {
  id: string
  index: number
  overlay: boolean
  isLast: boolean
}

/** A project symbol in the sidebar: the store is the source, the edits are commits. */
// biome-ignore lint/suspicious/noShadowRestrictedNames: Symbol is a domain component name
export const Symbol: React.FC<Props> = ({ id, index, overlay, isLast }) => {
  const dispatch = useDispatch<ThunkDispatch<AppState, unknown, Action>>()

  const ref = useRef<HTMLDivElement>(null)

  const { title, colorId, visual } = useSelector(
    migrationProjectSelector.getProjectSymbolData(id),
    shallowEqual
  )

  const missing = useSelector(
    migrationProjectSelector.getProjectSymbolMissing(id)
  )

  const isSelected = useSelector<AppState, boolean>(
    ({ canvas }) =>
      canvas.symbol.selectedSymbol === id && canvas.block.ids.length === 0
  )

  const isHighlighted = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.symbol.selectedSymbol === id
  )

  const isEditable = useSelector<AppState, boolean>(
    ({ canvas }) => canvas.symbol.selectedToEditSymbol === id
  )

  // The artboard is off and no edit of the symbol would be saved: the name
  // stays text, the toolbar locks.
  const artboardDisabled = useArtBoardDisabled()
  const editing = isEditable && !artboardDisabled

  const isImageUploading = useSelector<AppState, boolean>(
    ({ canvas }) =>
      canvas.uploadingImage.id === id && canvas.uploadingImage.uploading
  )

  const previewColorHex = useSelector<AppState, string | null>(
    (state) => getPreviewColor(state, id),
    shallowEqual
  )

  const colorHex = useSelector(
    migrationProjectSelector.getProjectColorForUI(colorId)
  )

  const libraryId = useSelector(
    migrationProjectSelector.getProjectSymbolLibraryId(id)
  )

  const color = previewColorHex || colorHex

  const onDragStart = useOnDragStart(ref, DRAGGABLE_TYPE.SYMBOL, id, {
    name: title,
  })

  // The toolbar's menus render in a portal outside the card.
  const onClickOutside = (e: MouseEvent) => {
    if (!editing && !isInsidePopover(e.target)) {
      dispatch({ type: CanvasActionTypes.CLEAR_SELECTED_SYMBOL })
    }
  }

  const onRename = (name: string) => {
    if (name !== title) {
      dispatch(thunkUpdateSymbolTitle({ id, title: name }))
    }

    dispatch({ type: CanvasActionTypes.RESET_EDIT_SYMBOL })
  }

  const onSubmit = () => {
    dispatch({ type: CanvasActionTypes.CLEAR_SELECTED_SYMBOL })
  }

  const onColor = (nextColorId: ID) => {
    dispatch(thunkUpdateSymbolColor({ id, colorId: nextColorId }))
  }

  const onWireframesChange = (wireframes: BlockWireframe[]) => {
    dispatch(thunkUpdateSymbolWireframes({ id, wireframes }))
  }

  const onImage = async (file: File) => {
    dispatch(setUploadingImageSymbol(true))

    try {
      await dispatch(thunkUploadSymbolImage({ id, file }))
    } catch (error) {
      console.error('upload image:', error)
    } finally {
      dispatch(setUploadingImageSymbol(false))
    }
  }

  const onRemoveImage = () => {
    dispatch(
      thunkUpdateSymbolVisual({
        id,
        visual: { type: 'wireframes', wireframes: [] },
      })
    )
  }

  const onDelete = () => {
    dispatch(thunkCheckUsageAndDeleteSymbol({ id }))
  }

  // A library ref comes back as a project symbol of its own, with the data
  // it renders with now.
  const onUnlink = () => {
    dispatch(thunkDetachSymbol({ id }))
  }

  if (missing) {
    return null
  }

  const cardProps = {
    id,
    index,
    componentRef: ref,
    title,
    color,
    visual,
    editing,
    highlighted: isHighlighted,
    uploading: isImageUploading,
    overlay,
    isLast,
    onDragStart,
    onRename,
    onSubmit,
  }

  if (!isSelected) {
    return <SymbolCard {...cardProps} note={<Note symbolId={id} />} />
  }

  return (
    <ClickAwayListener onClickAway={onClickOutside}>
      <SymbolCard {...cardProps} note={<Note symbolId={id} />}>
        {!overlay && (
          <SymbolToolbar
            anchorRef={ref}
            colorId={colorId}
            hasImage={visual.type === 'image'}
            deleteLabel="Delete"
            disabled={artboardDisabled}
            linked={libraryId !== null}
            onColor={onColor}
            onWireframesChange={onWireframesChange}
            onImage={onImage}
            onRemoveImage={onRemoveImage}
            onUnlink={onUnlink}
            onDelete={onDelete}
            uploading={isImageUploading}
            wireframes={visual.type === 'wireframes' ? visual.wireframes : []}
          />
        )}
      </SymbolCard>
    </ClickAwayListener>
  )
}
